import Pristine from 'pristinejs';
import { setLoading, wait } from './utils';
import { FileUpload } from './file-upload';

/*
 * Add the class 'js-form' to the form to get it to submit and be validated using Pristine
 * Add a 'data-route' attribute to the form to specify where it will submit to
 * Add a div.success-wrapper within the form for the success message
 * Add a div.error-wrapper within the form for any error messages
 */

/* -------------------------------------------------------------------------- */
/* Utilities                                                                  */
/* -------------------------------------------------------------------------- */

export const updateInputs = (form: HTMLFormElement, disable: boolean) => {
  const inputs = form.querySelectorAll(
    'input, textarea, select, button:not(.js-form-message-dismiss)'
  );

  inputs.forEach((input) => {
    if (disable) {
      input.setAttribute('disabled', 'true');
    } else {
      input.removeAttribute('disabled');
    }
  });
};

export const showSuccess = (form: HTMLFormElement, message?: string) => {
  const successWrapper = form.querySelector('.js-form-success') as HTMLElement | null;
  if (!successWrapper) {
    return;
  }

  const text = successWrapper.querySelector('.form__message-text');

  if (message && text) {
    text.innerHTML = message;
  }

  successWrapper.removeAttribute('aria-hidden');
};

export const showError = (form: HTMLFormElement) => {
  const errorsWrapper = form.querySelector('.js-form-error') as HTMLElement | null;
  if (!errorsWrapper) {
    return;
  }

  errorsWrapper.removeAttribute('aria-hidden');
};

export const resetForm = (
  form: HTMLFormElement,
  pristine?: Pristine,
  resetHidden: boolean = false
) => {
  updateInputs(form, false);

  form.querySelector('.js-form-success')?.setAttribute('aria-hidden', 'true');
  form.querySelector('.js-form-error')?.setAttribute('aria-hidden', 'true');

  if (resetHidden) {
    form
      .querySelectorAll<HTMLInputElement>('input[type="hidden"]')
      .forEach((input) => (input.value = ''));
  }

  const submit = form.querySelector<HTMLButtonElement>('[type=submit]');
  submit?.classList.remove('btn--loading');
  if (submit) {
    submit.disabled = false;
  }

  pristine?.reset();
  form.reset();
};

function hideAllMsgs(form: HTMLFormElement) {
  form.querySelector('.success-wrapper')?.replaceChildren();
  form.querySelector('.errors-wrapper')?.replaceChildren();
}

/* -------------------------------------------------------------------------- */
/* Init: Floating labels + resets                                              */
/* -------------------------------------------------------------------------- */

function initFloatingLabels() {
  const floatingLabels = document.querySelectorAll('.form__label--floating');

  floatingLabels.forEach((label) => {
    const next = label.nextElementSibling as HTMLElement;
    const input =
      next?.tagName === 'DIV'
        ? next.querySelector('input, select')
        : next;

    if (!input) {
      return;
    }

    const el = input as HTMLInputElement | HTMLSelectElement;

    // Date inputs show the browser's own placeholder, so their label always sits above the field.
    if (el.type === 'date') {
      label.classList.add('form__label--active');
      return;
    }

    if (el.value) {
      label.classList.add('form__label--active');
    }

    el.addEventListener('focus', () =>
      label.classList.add('form__label--active')
    );

    el.addEventListener('blur', () => {
      if (!el.value) {
        label.classList.remove('form__label--active');
      }
    });

    el.addEventListener('change', () => {
      label.classList.toggle('form__label--active', !!el.value);
    });
  });

  document.querySelectorAll('form').forEach((form) => {
    form.addEventListener('reset', () => {
      setTimeout(() => {
        form
          .querySelectorAll('.form__label--floating')
          .forEach((label) => label.classList.remove('form__label--active'));
      }, 0);
    });
  });
}

/* -------------------------------------------------------------------------- */
/* Init: File uploads                                                          */
/* -------------------------------------------------------------------------- */

function initFileUploads() {
  document
    .querySelectorAll<HTMLElement>('.js-file-upload')
    .forEach((el) => new FileUpload(el));
}

/* -------------------------------------------------------------------------- */
/* Init: Forms                                                                 */
/* -------------------------------------------------------------------------- */

export function initForms() {
  const csrfToken = document
    .querySelector('meta[name="csrf-token"]')
    ?.getAttribute('content') ?? '';

  initFloatingLabels();
  initFileUploads();

  const forms = document.querySelectorAll<HTMLFormElement>('.js-form');

  forms.forEach((form) => {
    // 🔒 Prevent double-init
    if (form.dataset.initialized === 'true') {
      return;
    }
    form.dataset.initialized = 'true';

    const pristine = new Pristine(form, {
      classTo: 'form__input-wrap',
      errorTextParent: 'form__input-wrap',
    });

    // Message dismiss buttons
    form.querySelectorAll<HTMLElement>('.form__message').forEach((message) => {
      message
        .querySelector('.js-form-message-dismiss')
        ?.addEventListener('click', (e) => {
          e.preventDefault();
          resetForm(form, pristine);
          message.setAttribute('aria-hidden', 'true');
        });
    });

    // Show / hide fields
    form
      .querySelectorAll<HTMLInputElement>('[data-show], [data-hide]')
      .forEach((field) => {
        field.addEventListener('change', () => {
          const name = field.dataset.show || field.dataset.hide;
          const related = name
            ? form.querySelector<HTMLElement>(`[name="${name}"]`)
            : null;

          if (!related) {
            return;
          }

          if (field.dataset.show) {
            related.classList.remove('hidden');
            related.removeAttribute('disabled');
          } else {
            related.classList.add('hidden');
            related.setAttribute('disabled', 'true');
          }
        });
      });

    // Submit handler
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      hideAllMsgs(form);

      const honey = form.querySelector<HTMLInputElement>('input[name="age"]');
      const valid = pristine.validate() && !honey?.value;

      if (!valid) {
        return;
      }

      const route = form.dataset.route!;
      const formData = new FormData(form);
      formData.append('_csrf', csrfToken);

      updateInputs(form, true);
      setLoading(form, true);

      try {
        const response = await fetch(route, {
          method: 'POST',
          credentials: 'same-origin',
          body: formData,
          headers: { accept: 'application/json' },
        });

        await wait(300);

        if (!response.ok) {
          showError(form);
          return;
        }

        showSuccess(form);

        if (form.dataset.autoReset) {
          setTimeout(
            () => resetForm(form, pristine),
            Number(form.dataset.autoReset) * 1000
          );
        }
      } catch (err) {
        console.error(err);
        showError(form);
      } finally {
        setLoading(form, false);
      }
    });
  });
}
